import { useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseFieldErrors, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { useAnthem } from "@/components/anthem-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Camera, Loader2, Monitor, Smartphone, Globe, Users, LogOut, Mail, ShieldOff, CheckCircle2, Sun, Moon, FlaskConical, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { JOURNEY_STAGES, JOURNEY_STAGE_LABELS, type JourneyStage } from "@shared/schema";
import { ACCOUNT_TIMEZONES, DEFAULT_ACCOUNT_TIMEZONE } from "@/lib/timezones";
import { useTheme } from "@/components/theme-provider";
import { usePageTitle } from "@/hooks/use-page-title";
import { AssignmentNotificationsSection } from "@/components/assignment-notifications-section";

type ActiveMembership = {
  ownerUserId: number;
  ownerName: string;
  ownerEmail: string;
  role: string;
};

type AuthMeData = {
  user: { id: number; email: string; selectedPlan?: string; isAdmin?: boolean; totpEnabled?: boolean };
  activeMembership?: ActiveMembership | null;
  effectivePlan?: string;
  planInherited?: boolean;
  planInviterName?: string | null;
};

type ProfileData = {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  profilePictureUrl: string | null;
  referralCode: string | null;
  hasPassword: boolean;
  journeyStage: JourneyStage | null;
  teamEmailNotificationsEnabled: boolean;
  unmatchedSmsEmailEnabled: boolean;
  messagingPhoneNumber: string | null;
  padsplitPushFailureEmailEnabled: boolean;
  mercuryStatementsEmailEnabled: boolean;
  mercuryCardNearLimitEmailEnabled: boolean;
  sopLink: string | null;
  excludeInactiveRoomsFromOccupancy: boolean;
  showInactiveMessages: boolean;
  timezone: string | null;
  assignmentNotificationPrefs?: unknown;
  phoneVerified?: boolean;
  smsConsent?: boolean;
};

const COUNTRY_CODES = [
  { code: "+1", label: "US +1" },
  { code: "+44", label: "UK +44" },
  { code: "+91", label: "IN +91" },
  { code: "+61", label: "AU +61" },
  { code: "+49", label: "DE +49" },
  { code: "+33", label: "FR +33" },
  { code: "+81", label: "JP +81" },
  { code: "+86", label: "CN +86" },
  { code: "+55", label: "BR +55" },
  { code: "+52", label: "MX +52" },
];

export default function AppProfile() {
  usePageTitle("Profile");
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "personal", {
    tabs: ["personal", "membership", "notifications", "security", "appearance", "early-access"],
  });

  const profileQuery = useQuery<ProfileData>({
    queryKey: ["/api/app/profile"],
  });

  const authMeQuery = useQuery<AuthMeData>({
    queryKey: ["/api/app/auth/me"],
  });

  const activeMembership = authMeQuery.data?.activeMembership || null;

  if (profileQuery.isLoading) {
    return (
      <AppLayout>
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="py-12 text-center text-muted-foreground">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  if (!profileQuery.data) {
    return (
      <AppLayout>
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="py-12 text-center text-muted-foreground">Failed to load profile.</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="text-profile-title" data-tour="profile-heading">Your profile</h1>
            <p className="text-muted-foreground mt-1">Manage your profile information and settings.</p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="profile" />
            <GuideButton pageKey="profile" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="profile" />

        <div className="flex gap-6 mt-6 border-b border-border">
          <button
            onClick={() => setActiveTab("personal")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "personal" ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-personal-info"
            data-tour="tab-btn-personal"
          >
            Personal info
          </button>
          {activeMembership && (
            <button
              onClick={() => setActiveTab("membership")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "membership" ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-membership"
            >
              Team membership
            </button>
          )}
          <button
            onClick={() => setActiveTab("notifications")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "notifications" ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-notifications"
            data-tour="tab-btn-notifications"
          >
            Notifications
          </button>
          <button
            onClick={() => setActiveTab("security")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "security" ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-security"
            data-tour="tab-btn-security"
          >
            Security
          </button>
          <button
            onClick={() => setActiveTab("appearance")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "appearance" ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-appearance"
            data-tour="tab-btn-appearance"
          >
            Appearance
          </button>
          <button
            onClick={() => setActiveTab("early-access")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "early-access" ? "border-blue-600 text-blue-600" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-early-access"
            data-tour="tab-btn-early-access"
          >
            Early Access
          </button>
        </div>

        {activeTab === "personal" && (
          <PersonalInfoTab
            profile={profileQuery.data}
            effectivePlan={authMeQuery.data?.effectivePlan || authMeQuery.data?.user?.selectedPlan || null}
            planInherited={!!authMeQuery.data?.planInherited}
            planInviterName={authMeQuery.data?.planInviterName || null}
            activeMembership={activeMembership}
          />
        )}
        {activeTab === "membership" && activeMembership && <MembershipTab membership={activeMembership} />}
        {activeTab === "notifications" && <NotificationsTab profile={profileQuery.data} />}
        {activeTab === "security" && <SecurityTab profile={profileQuery.data} isAdmin={!!authMeQuery.data?.user?.isAdmin} />}
        {activeTab === "appearance" && <AppearanceTab />}
        {activeTab === "early-access" && <EarlyAccessTab />}
      </div>
    </AppLayout>
  );
}

function PersonalInfoTab({
  profile,
  effectivePlan,
  planInherited,
  planInviterName,
  activeMembership,
}: {
  profile: ProfileData;
  effectivePlan: string | null;
  planInherited: boolean;
  planInviterName: string | null;
  activeMembership: ActiveMembership | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState(profile.firstName || "");
  const [lastName, setLastName] = useState(profile.lastName || "");
  const parsedPhone = (() => {
    const p = (profile.phone || "").trim();
    if (p.startsWith("+")) {
      const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
      const known = sortedCodes.find((cc) => p.startsWith(cc.code));
      if (known) return { code: known.code, number: p.slice(known.code.length) };
      const match = p.match(/^(\+\d{1,3})(.*)$/);
      if (match) return { code: match[1], number: match[2] };
    }
    return { code: "+1", number: p };
  })();
  const [phone, setPhone] = useState(parsedPhone.number);
  const [countryCode, setCountryCode] = useState(parsedPhone.code);
  const [journeyStage, setJourneyStage] = useState<JourneyStage | "">(profile.journeyStage || "");
  const [profilePicture, setProfilePicture] = useState<string | null>(profile.profilePictureUrl || null);
  const [sopLink, setSopLink] = useState(profile.sopLink || "");
  const [timezone, setTimezone] = useState(profile.timezone || DEFAULT_ACCOUNT_TIMEZONE);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pendingPictureUrl, setPendingPictureUrl] = useState<string | null>(null);
  const [picturePreview, setPicturePreview] = useState<string | null>(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, string | null>) => {
      const res = await apiRequest("PATCH", "/api/app/profile", data);
      return res.json();
    },
    onSuccess: (data) => {
      setFieldErrors({});
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      if (data?.profilePictureUrl) setProfilePicture(data.profilePictureUrl);
      setPendingPictureUrl(null);
      if (picturePreview) URL.revokeObjectURL(picturePreview);
      setPicturePreview(null);
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    },
    onError: (err: Error) => {
      const next = parseFieldErrors(err);
      if (Object.keys(next).length > 0) {
        setFieldErrors(next);
        toast({
          title: "Please fix the highlighted fields",
          description: Object.values(next).join(" "),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to update profile."),
        variant: "destructive",
      });
    },
  });

  function handleSave() {
    setFieldErrors({});
    const payload: Record<string, unknown> = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim() ? `${countryCode}${phone.trim()}` : "",
    };
    if (pendingPictureUrl) {
      payload.profilePictureUrl = pendingPictureUrl;
    }
    if (journeyStage) {
      payload.journeyStage = journeyStage;
    }
    payload.sopLink = sopLink.trim() || null;
    payload.timezone = timezone;
    updateMutation.mutate(payload as any);
  }

  function handleCancel() {
    setFirstName(profile.firstName || "");
    setLastName(profile.lastName || "");
    const parsed = (() => {
      const p = profile.phone || "";
      const match = p.match(/^(\+\d{1,3})(.*)$/);
      if (match) return { code: match[1], number: match[2] };
      return { code: "+1", number: p };
    })();
    setPhone(parsed.number);
    setCountryCode(parsed.code);
    setJourneyStage(profile.journeyStage || "");
    setProfilePicture(profile.profilePictureUrl || null);
    setSopLink(profile.sopLink || "");
    setTimezone(profile.timezone || DEFAULT_ACCOUNT_TIMEZONE);
    setFieldErrors({});
    setPendingPictureUrl(null);
    if (picturePreview) URL.revokeObjectURL(picturePreview);
    setPicturePreview(null);
  }

  function clearFieldError(field: string) {
    if (!fieldErrors[field]) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Please upload an image under 10MB.", variant: "destructive" });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please choose an image file.", variant: "destructive" });
      return;
    }
    setIsUploadingPicture(true);
    try {
      const uploadRes = await fetch("/api/app/profile/picture", {
        method: "POST",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
        credentials: "include",
      });
      if (!uploadRes.ok) {
        let msg = "Upload failed";
        try { msg = (await uploadRes.json()).message || msg; } catch {}
        throw new Error(msg);
      }
      const { objectPath } = await uploadRes.json();
      const previewUrl = URL.createObjectURL(file);
      if (picturePreview) URL.revokeObjectURL(picturePreview);
      setPicturePreview(previewUrl);
      setPendingPictureUrl(objectPath);
    } catch (err) {
      toast({ title: "Upload failed", description: "Could not upload the image. Please try again.", variant: "destructive" });
    } finally {
      setIsUploadingPicture(false);
      if (e.target) e.target.value = "";
    }
  }

  const initials = [firstName?.[0], lastName?.[0]].filter(Boolean).join("").toUpperCase() || profile.email[0].toUpperCase();

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-lg font-semibold text-foreground" data-testid="text-personal-info-title" data-tour="profile-info">Personal Info</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCancel} data-testid="button-cancel-profile">Cancel</Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending} className="bg-brand hover:bg-brand-hover" data-testid="button-save-profile" data-tour="profile-save">
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-6 mb-8">
        <div className="relative inline-block">
          {picturePreview || profilePicture ? (
            <img src={picturePreview || profilePicture || undefined} alt="Profile" className="h-20 w-20 rounded-full object-cover" data-testid="img-profile-picture" />
          ) : (
            <div className="h-20 w-20 rounded-full bg-brand flex items-center justify-center text-white text-2xl font-medium" data-testid="img-profile-picture">
              {initials}
            </div>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingPicture}
            className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-background border border-border flex items-center justify-center shadow-sm hover:bg-accent disabled:opacity-60"
            data-testid="button-edit-picture"
          >
            {isUploadingPicture ? <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" /> : <Camera className="h-3.5 w-3.5 text-muted-foreground" />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            data-testid="input-profile-picture"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 max-w-xl">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">First name</label>
          <Input
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); clearFieldError("firstName"); }}
            placeholder="First name"
            className={fieldErrors.firstName ? "border-red-500 focus-visible:ring-red-500" : ""}
            data-testid="input-first-name"
          />
          {fieldErrors.firstName && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-first-name">{fieldErrors.firstName}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Last name</label>
          <Input
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); clearFieldError("lastName"); }}
            placeholder="Last name"
            className={fieldErrors.lastName ? "border-red-500 focus-visible:ring-red-500" : ""}
            data-testid="input-last-name"
          />
          {fieldErrors.lastName && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-last-name">{fieldErrors.lastName}</p>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
          <div className="flex gap-2">
            <Input value={profile.email} disabled className="bg-muted flex-1" data-testid="input-email" />
            <ChangeEmailDialog currentEmail={profile.email} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">You will receive all emails to this address.</p>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">Phone number</label>
          <div className="flex gap-2">
            <select
              value={countryCode}
              onChange={(e) => { setCountryCode(e.target.value); clearFieldError("phone"); }}
              className="h-10 rounded-md border border-border px-3 text-sm bg-background text-foreground"
              data-testid="select-country-code"
            >
              {COUNTRY_CODES.map((cc) => (
                <option key={cc.code} value={cc.code}>{cc.label}</option>
              ))}
            </select>
            <Input
              value={phone}
              onChange={(e) => { setPhone(e.target.value); clearFieldError("phone"); }}
              placeholder="Phone number"
              className={`flex-1 ${fieldErrors.phone ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              data-testid="input-phone"
            />
          </div>
          {fieldErrors.phone && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-phone">{fieldErrors.phone}</p>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">Where you are in your CoLiving journey</label>
          <Select
            value={journeyStage || undefined}
            onValueChange={(val) => { setJourneyStage(val as JourneyStage); clearFieldError("journeyStage"); }}
          >
            <SelectTrigger
              className={fieldErrors.journeyStage ? "border-red-500 focus-visible:ring-red-500" : ""}
              data-testid="select-journey-stage"
            >
              <SelectValue placeholder="Select your current stage" />
            </SelectTrigger>
            <SelectContent>
              {JOURNEY_STAGES.map((stage) => (
                <SelectItem key={stage} value={stage} data-testid={`option-journey-${stage}`}>
                  {JOURNEY_STAGE_LABELS[stage]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">Update this as you progress so your dashboard guidance stays relevant.</p>
          {fieldErrors.journeyStage && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-journey-stage">{fieldErrors.journeyStage}</p>
          )}
          <JourneyHelpBoxControl />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Company SOP link</label>
          <Input
            value={sopLink}
            onChange={(e) => { setSopLink(e.target.value); clearFieldError("sopLink"); }}
            placeholder="https://... (Google Drive, Notion, etc.)"
            className={fieldErrors.sopLink ? "border-red-500 focus-visible:ring-red-500" : ""}
            data-testid="input-company-sop-link"
          />
          <p className="text-xs text-gray-500 mt-1">Company-wide Standard Operating Procedures link, used as a fallback when a property has no SOP link.</p>
          {fieldErrors.sopLink && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-company-sop-link">{fieldErrors.sopLink}</p>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">Timezone</label>
          <Select
            value={timezone}
            onValueChange={(val) => { setTimezone(val); clearFieldError("timezone"); }}
          >
            <SelectTrigger
              className={fieldErrors.timezone ? "border-red-500 focus-visible:ring-red-500" : ""}
              data-testid="select-account-timezone"
            >
              <SelectValue placeholder="Select your timezone" />
            </SelectTrigger>
            <SelectContent>
              {ACCOUNT_TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value} data-testid={`option-account-timezone-${tz.value}`}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">Set this once for your account. Scheduled reminders that fire in your local time — like smart-lock low-battery alerts and service-form schedules — use this timezone by default.</p>
          {fieldErrors.timezone && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-account-timezone">{fieldErrors.timezone}</p>
          )}
        </div>
      </div>

      <PlanSection
        effectivePlan={effectivePlan}
        planInherited={planInherited}
        planInviterName={planInviterName}
        activeMembership={activeMembership}
      />
    </div>
  );
}

function PlanSection({
  effectivePlan,
  planInherited,
  planInviterName,
  activeMembership,
}: {
  effectivePlan: string | null;
  planInherited: boolean;
  planInviterName: string | null;
  activeMembership: ActiveMembership | null;
}) {
  const planLabel = effectivePlan || "Host";
  const ownerName = planInviterName || activeMembership?.ownerName || null;
  const isInherited = planInherited && !!ownerName;
  const isTeamMember = !!activeMembership;

  return (
    <div className="mt-10 max-w-xl">
      <h2 className="text-lg font-semibold text-foreground mb-3" data-testid="text-plan-section-title">
        Subscription plan
      </h2>
      <div className="rounded-lg border border-border p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-full ${
                  isInherited
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "bg-muted text-foreground"
                }`}
                data-testid="badge-profile-plan"
                title={isInherited && ownerName ? `Plan inherited from ${ownerName}` : undefined}
              >
                {planLabel}
                {isInherited && ownerName && (
                  <span className="text-blue-600" data-testid="text-profile-plan-inherited-from">
                    — via {ownerName}
                  </span>
                )}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {isTeamMember
                ? `You inherit this plan from ${ownerName || "your team owner"}. Plan changes are managed by them.`
                : "Your current subscription tier."}
            </p>
          </div>
          {!isTeamMember && (
            <Link href="/app/account/billing">
              <Button
                variant="outline"
                className="border-blue-200 text-brand hover:bg-blue-50 hover:text-brand flex-shrink-0"
                data-testid="button-upgrade-plan"
              >
                Upgrade plan
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function JourneyHelpBoxControl() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const dismissedQuery = useQuery<{ dismissedStages: string[] }>({
    queryKey: ["/api/app/dashboard/dismissed-journey-stages"],
  });
  const restoreMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/app/dashboard/dismissed-journey-stages");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/dashboard/dismissed-journey-stages"] });
      toast({ title: "Journey help box restored", description: "It will reappear on your dashboard." });
    },
    onError: (err: Error) => {
      toast({ title: "Could not restore help box", description: parseErrorMessage(err, "Couldn't restore help box. Please try again."), variant: "destructive" });
    },
  });

  const dismissedCount = dismissedQuery.data?.dismissedStages?.length ?? 0;
  const hasDismissed = dismissedCount > 0;

  return (
    <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-border bg-muted px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground" data-testid="text-journey-help-box-label">Journey help box on dashboard</p>
        <p className="text-xs text-muted-foreground">
          {hasDismissed
            ? "You've dismissed the help box. Restore it to see stage-specific guidance again."
            : "Showing on your dashboard. Dismiss it from the dashboard if you don't want to see it."}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!hasDismissed || restoreMutation.isPending}
        onClick={() => restoreMutation.mutate()}
        data-testid="button-restore-journey-help-box"
      >
        {restoreMutation.isPending ? "Restoring..." : "Show help box"}
      </Button>
    </div>
  );
}

function NotificationsTab({ profile }: { profile: ProfileData }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [enabled, setEnabled] = useState<boolean>(profile.teamEmailNotificationsEnabled !== false);
  const [unmatchedEnabled, setUnmatchedEnabled] = useState<boolean>(
    profile.unmatchedSmsEmailEnabled !== false,
  );
  const [padsplitDigestEnabled, setPadsplitDigestEnabled] = useState<boolean>(
    profile.padsplitPushFailureEmailEnabled !== false,
  );
  const [mercuryStatementsEnabled, setMercuryStatementsEnabled] = useState<boolean>(
    profile.mercuryStatementsEmailEnabled !== false,
  );
  const [mercuryNearLimitEnabled, setMercuryNearLimitEnabled] = useState<boolean>(
    profile.mercuryCardNearLimitEmailEnabled !== false,
  );
  const [excludeInactive, setExcludeInactive] = useState<boolean>(
    profile.excludeInactiveRoomsFromOccupancy === true,
  );
  const [showInactiveMessages, setShowInactiveMessages] = useState<boolean>(
    profile.showInactiveMessages === true,
  );

  const mutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", { teamEmailNotificationsEnabled: next });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      const next = data?.teamEmailNotificationsEnabled !== false;
      setEnabled(next);
      toast({
        title: next ? "Team activity emails enabled" : "Team activity emails muted",
        description: next
          ? "You'll receive emails for team invitations and activity."
          : "You'll keep getting in-app notifications, but no emails for team activity.",
      });
    },
    onError: (err: Error) => {
      setEnabled(profile.teamEmailNotificationsEnabled !== false);
      toast({
        title: "Could not update preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const unmatchedMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", { unmatchedSmsEmailEnabled: next });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      const next = data?.unmatchedSmsEmailEnabled !== false;
      setUnmatchedEnabled(next);
      toast({
        title: next ? "Unknown-number SMS emails enabled" : "Unknown-number SMS emails muted",
        description: next
          ? "You'll get an email when a phone we don't recognize texts your messaging line."
          : "You'll keep getting in-app notifications, but no emails for unknown-number texts.",
      });
    },
    onError: (err: Error) => {
      setUnmatchedEnabled(profile.unmatchedSmsEmailEnabled !== false);
      toast({
        title: "Could not update preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  function handleToggle(next: boolean) {
    setEnabled(next);
    mutation.mutate(next);
  }

  function handleUnmatchedToggle(next: boolean) {
    setUnmatchedEnabled(next);
    unmatchedMutation.mutate(next);
  }

  const padsplitDigestMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", { padsplitPushFailureEmailEnabled: next });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      const next = data?.padsplitPushFailureEmailEnabled !== false;
      setPadsplitDigestEnabled(next);
      toast({
        title: next ? "PadSplit failure digest enabled" : "PadSplit failure digest muted",
        description: next
          ? "You'll get a daily email rolling up any PadSplit pushes that haven't synced."
          : "You'll keep getting in-app notifications, but no daily email digest of PadSplit failures.",
      });
    },
    onError: (err: Error) => {
      setPadsplitDigestEnabled(profile.padsplitPushFailureEmailEnabled !== false);
      toast({
        title: "Could not update preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  function handlePadsplitDigestToggle(next: boolean) {
    setPadsplitDigestEnabled(next);
    padsplitDigestMutation.mutate(next);
  }

  const mercuryStatementsMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", { mercuryStatementsEmailEnabled: next });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      const next = data?.mercuryStatementsEmailEnabled !== false;
      setMercuryStatementsEnabled(next);
      toast({
        title: next ? "Mercury statements digest enabled" : "Mercury statements digest muted",
        description: next
          ? "You'll get an email summarizing any new Mercury statements when the monthly sync runs."
          : "You'll keep getting in-app notifications, but no monthly email when new Mercury statements arrive.",
      });
    },
    onError: (err: Error) => {
      setMercuryStatementsEnabled(profile.mercuryStatementsEmailEnabled !== false);
      toast({
        title: "Could not update preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  function handleMercuryStatementsToggle(next: boolean) {
    setMercuryStatementsEnabled(next);
    mercuryStatementsMutation.mutate(next);
  }

  const mercuryNearLimitMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", { mercuryCardNearLimitEmailEnabled: next });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      const next = data?.mercuryCardNearLimitEmailEnabled !== false;
      setMercuryNearLimitEnabled(next);
      toast({
        title: next ? "Card near-limit alerts enabled" : "Card near-limit alerts muted",
        description: next
          ? "You'll get an email when a Mercury credit card crosses 80% of its limit."
          : "You'll keep getting the in-app alert, but no email when a Mercury card nears its limit.",
      });
    },
    onError: (err: Error) => {
      setMercuryNearLimitEnabled(profile.mercuryCardNearLimitEmailEnabled !== false);
      toast({
        title: "Could not update preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  function handleMercuryNearLimitToggle(next: boolean) {
    setMercuryNearLimitEnabled(next);
    mercuryNearLimitMutation.mutate(next);
  }

  const excludeInactiveMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", { excludeInactiveRoomsFromOccupancy: next });
      return res.json();
    },
    onSuccess: (data, nextVal) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      const next = data?.excludeInactiveRoomsFromOccupancy ?? nextVal;
      setExcludeInactive(next === true);
      toast({
        title: next ? "Inactive rooms excluded from occupancy" : "All rooms included in occupancy",
        description: next
          ? "Occupancy % will be calculated using only active rooms."
          : "Occupancy % will be calculated using all rooms, including inactive ones.",
      });
    },
    onError: (err: Error) => {
      setExcludeInactive(profile.excludeInactiveRoomsFromOccupancy === true);
      toast({
        title: "Could not update preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const showInactiveMessagesMutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PATCH", "/api/app/profile", { showInactiveMessages: next });
      return res.json();
    },
    onSuccess: (data, nextVal) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      const next = data?.showInactiveMessages ?? nextVal;
      setShowInactiveMessages(next === true);
      toast({
        title: next ? "Inactive conversations shown" : "Inactive conversations hidden",
        description: next
          ? "Moved-out and terminated members will appear in your Messages inbox and search."
          : "Only active members appear in your Messages inbox. Use the Unresolved filter or search to find inactive conversations.",
      });
    },
    onError: (err: Error) => {
      setShowInactiveMessages(profile.showInactiveMessages === true);
      toast({
        title: "Could not update preference",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  function handleShowInactiveMessagesToggle(next: boolean) {
    setShowInactiveMessages(next);
    showInactiveMessagesMutation.mutate(next);
  }

  function handleExcludeInactiveToggle(next: boolean) {
    setExcludeInactive(next);
    excludeInactiveMutation.mutate(next);
  }

  return (
    <div className="mt-8 max-w-xl">
      <h2 className="text-lg font-semibold text-foreground" data-testid="text-notifications-title">
        Email notifications
      </h2>
      <p className="text-sm text-muted-foreground mt-1">
        Choose which activity emails PadPulse sends you. In-app notifications stay on regardless.
      </p>

      <div className="mt-6 rounded-lg border border-border p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid="text-team-email-toggle-label">
            Team activity emails
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Emails when invitations are accepted or declined and when team members leave your team. You'll still see these in your in-app notifications.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          disabled={mutation.isPending}
          data-testid="switch-team-email-notifications"
        />
      </div>

      {profile.messagingPhoneNumber ? (
        <div className="mt-4 rounded-lg border border-border p-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground" data-testid="text-unmatched-sms-email-toggle-label">
              SMS from unknown numbers
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Email me when a phone number we don't recognize texts my PadPulse messaging line so I can triage it. The in-app notification on the bell icon fires regardless.
            </p>
          </div>
          <Switch
            checked={unmatchedEnabled}
            onCheckedChange={handleUnmatchedToggle}
            disabled={unmatchedMutation.isPending}
            data-testid="switch-unmatched-sms-email-notifications"
          />
        </div>
      ) : null}

      <div className="mt-4 rounded-lg border border-border p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid="text-padsplit-digest-email-toggle-label">
            Daily PadSplit failure digest
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Once-a-day email rollup of any maintenance tasks that failed to push to PadSplit and are still unresolved. The in-app PadSplit failure notifications stay on regardless.
          </p>
        </div>
        <Switch
          checked={padsplitDigestEnabled}
          onCheckedChange={handlePadsplitDigestToggle}
          disabled={padsplitDigestMutation.isPending}
          data-testid="switch-padsplit-push-failure-digest-email"
        />
      </div>

      <div className="mt-4 rounded-lg border border-border p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid="text-mercury-statements-email-toggle-label">
            New Mercury statements digest
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Email me when the monthly Mercury sync files new bank statements to my account, with a link to the Statements tab. The in-app notifications stay on regardless.
          </p>
        </div>
        <Switch
          checked={mercuryStatementsEnabled}
          onCheckedChange={handleMercuryStatementsToggle}
          disabled={mercuryStatementsMutation.isPending}
          data-testid="switch-mercury-statements-email"
        />
      </div>

      <div className="mt-4 rounded-lg border border-border p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid="text-mercury-near-limit-email-toggle-label">
            Credit card near-limit alert
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Email me when one of my Mercury credit cards crosses 80% of its limit, so I can pay it down before vendor payments get declined. The in-app alert stays on regardless.
          </p>
        </div>
        <Switch
          checked={mercuryNearLimitEnabled}
          onCheckedChange={handleMercuryNearLimitToggle}
          disabled={mercuryNearLimitMutation.isPending}
          data-testid="switch-mercury-card-near-limit-email"
        />
      </div>

      <AssignmentNotificationsSection
        prefs={profile.assignmentNotificationPrefs}
        phone={profile.phone}
        phoneVerified={profile.phoneVerified === true}
      />

      <h2 className="text-lg font-semibold text-foreground mt-10" data-testid="text-display-prefs-title">
        Display preferences
      </h2>
      <p className="text-sm text-muted-foreground mt-1">
        Control how occupancy figures are calculated across your dashboard and property views.
      </p>

      <div className="mt-6 rounded-lg border border-border p-5 flex items-start justify-between gap-4" data-tour="profile-notifications-occupancy">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid="text-exclude-inactive-rooms-label">
            Exclude inactive rooms from occupancy calculation
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            When on, rooms marked as inactive (e.g. under renovation or taken off-market) are excluded from your occupancy % and vacant-room counts everywhere in the app. Keeps your operational occupancy accurate when some rooms are intentionally offline.
          </p>
        </div>
        <Switch
          checked={excludeInactive}
          onCheckedChange={handleExcludeInactiveToggle}
          disabled={excludeInactiveMutation.isPending}
          data-testid="switch-exclude-inactive-rooms-occupancy"
        />
      </div>

      <h2 className="text-lg font-semibold text-foreground mt-10" data-testid="text-messages-prefs-title">
        Messages
      </h2>
      <p className="text-sm text-muted-foreground mt-1">
        Control what appears when you search your message inbox.
      </p>

      <div className="mt-6 rounded-lg border border-border p-5 flex items-start justify-between gap-4" data-tour="profile-show-inactive-messages">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid="text-show-inactive-messages-label">
            Show inactive messages
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            When on, inactive members (moved-out, terminated, or archived) appear in your Messages inbox and search results. When off, only currently housed residents appear. Hidden conversations are still reachable via the Unresolved filter, search, and direct links.
          </p>
        </div>
        <Switch
          checked={showInactiveMessages}
          onCheckedChange={handleShowInactiveMessagesToggle}
          disabled={showInactiveMessagesMutation.isPending}
          data-testid="switch-show-inactive-messages"
        />
      </div>
    </div>
  );
}

function AppearanceTab() {
  const { themePreference, setTheme } = useTheme();
  const { toast } = useToast();
  const { dismissed: anthemDismissed, dismiss: dismissAnthem, restore: restoreAnthem } = useAnthem();

  const handleSetTheme = (next: "light" | "dark" | "system") => {
    setTheme(next, () => {
      toast({
        title: "Couldn't save theme",
        description: "Your preference wasn't saved to your account. Please try again.",
        variant: "destructive",
      });
    });
  };

  const options: Array<{ value: "light" | "dark" | "system"; label: string; Icon: typeof Sun; testId: string }> = [
    { value: "light", label: "Light", Icon: Sun, testId: "button-theme-light" },
    { value: "dark", label: "Dark", Icon: Moon, testId: "button-theme-dark" },
    { value: "system", label: "Follow system", Icon: Monitor, testId: "button-theme-system" },
  ];

  return (
    <div className="mt-8 max-w-xl">
      <h2 className="text-lg font-semibold text-foreground" data-testid="text-appearance-title">
        Appearance
      </h2>
      <p className="text-sm text-muted-foreground mt-1">
        Choose your preferred interface theme. Your selection is saved to your account.
      </p>

      <div className="mt-6 grid grid-cols-3 gap-3" data-tour="profile-appearance-theme">
        {options.map(({ value, label, Icon, testId }) => {
          const selected = themePreference === value;
          return (
            <button
              key={value}
              onClick={() => handleSetTheme(value)}
              className={`relative flex flex-col items-center gap-3 rounded-lg border-2 p-5 transition-colors hover-elevate ${
                selected
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:border-muted-foreground/40"
              }`}
              data-testid={testId}
            >
              <Icon className={`h-7 w-7 ${selected ? "text-primary" : "text-muted-foreground"}`} />
              <span className={`text-sm font-medium ${selected ? "text-primary" : "text-foreground"}`}>
                {label}
              </span>
              {selected && (
                <span className="absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-10 flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
        <div>
          <label
            htmlFor="switch-show-anthem"
            className="text-sm font-medium text-foreground"
          >
            Show PadPulse Anthem button
          </label>
          <p className="text-sm text-muted-foreground mt-1">
            Show the PadPulse Anthem prompt in your dashboard topbar. Turn it back
            on here if you previously hid it.
          </p>
        </div>
        <Switch
          id="switch-show-anthem"
          checked={!anthemDismissed}
          onCheckedChange={(checked) => (checked ? restoreAnthem() : dismissAnthem())}
          data-testid="switch-show-anthem"
        />
      </div>
    </div>
  );
}

type BetaFeatureInfo = {
  id: number;
  key: string;
  name: string;
  description: string | null;
  isActive: boolean;
  allowSelfOptIn: boolean;
};

function EarlyAccessTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const accessKeysQuery = useQuery<string[]>({
    queryKey: ["/api/app/beta/access"],
  });

  const optInBetasQuery = useQuery<BetaFeatureInfo[]>({
    queryKey: ["/api/app/beta"],
  });

  const joinMutation = useMutation({
    mutationFn: async (key: string) => {
      await apiRequest("POST", `/api/app/beta/${key}/join`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/beta/access"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/beta"] });
      toast({ title: "You're in!", description: "You now have early access to this feature." });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const leaveMutation = useMutation({
    mutationFn: async (key: string) => {
      await apiRequest("DELETE", `/api/app/beta/${key}/leave`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/beta/access"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/beta"] });
      toast({ title: "Left beta", description: "You've opted out of this early access feature." });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const myKeys = new Set(accessKeysQuery.data ?? []);
  const optInBetas = optInBetasQuery.data ?? [];
  const isLoading = accessKeysQuery.isLoading || optInBetasQuery.isLoading;

  return (
    <div className="mt-8 max-w-xl" data-tour="profile-early-access-panel">
      <div className="flex items-center gap-2 mb-1">
        <FlaskConical className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-foreground" data-testid="text-early-access-title">
          Early Access
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Opt in to experimental features before they're released to everyone. You can leave at any time.
      </p>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-lg border bg-muted/30 animate-pulse" />
          ))}
        </div>
      ) : optInBetas.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <FlaskConical className="mx-auto h-8 w-8 text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No early access programs are currently open.</p>
          <p className="text-xs text-muted-foreground/70 mt-1">Check back soon — new betas are added regularly.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {optInBetas.map((beta) => {
            const enrolled = myKeys.has(beta.key);
            return (
              <div
                key={beta.key}
                className={`rounded-lg border p-4 flex items-start justify-between gap-4 transition-colors ${enrolled ? "border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20" : "bg-card"}`}
                data-testid={`early-access-card-${beta.key}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{beta.name}</span>
                    {enrolled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-300" data-testid={`badge-enrolled-${beta.key}`}>
                        <Check className="h-3 w-3" />
                        Enrolled
                      </span>
                    )}
                  </div>
                  {beta.description && (
                    <p className="text-xs text-muted-foreground mt-1">{beta.description}</p>
                  )}
                </div>
                <button
                  onClick={() => enrolled ? leaveMutation.mutate(beta.key) : joinMutation.mutate(beta.key)}
                  disabled={joinMutation.isPending || leaveMutation.isPending}
                  className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                    enrolled
                      ? "bg-muted hover:bg-muted/80 text-muted-foreground"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                  data-testid={enrolled ? `button-leave-beta-${beta.key}` : `button-join-beta-${beta.key}`}
                >
                  {enrolled ? "Leave" : "Join"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChangePasswordForm({ hasPassword }: { hasPassword: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removePassword, setRemovePassword] = useState("");
  const [removeError, setRemoveError] = useState<string | null>(null);

  const removeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/app/profile/password", {
        currentPassword: removePassword,
      });
      return res.json();
    },
    onSuccess: () => {
      setRemoveOpen(false);
      setRemovePassword("");
      setRemoveError(null);
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      toast({ title: "Password removed", description: "You'll sign in with email codes from now on." });
    },
    onError: (err: Error) => {
      const message = parseErrorMessage(err, "Failed to remove password.");
      setRemoveError(message);
    },
  });

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/profile/password", {
        currentPassword,
        newPassword,
      });
      return res.json();
    },
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setFieldErrors({});
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      toast({ title: hasPassword ? "Password updated" : "Password set", description: "Your new password is active." });
    },
    onError: (err: Error) => {
      const next = parseFieldErrors(err);
      if (Object.keys(next).length > 0) {
        setFieldErrors(next);
        toast({
          title: "Please fix the highlighted fields",
          description: Object.values(next).join(" "),
          variant: "destructive",
        });
        return;
      }
      const message = parseErrorMessage(err, "Failed to update password.");
      if (/current password/i.test(message)) {
        setFieldErrors({ currentPassword: message });
      }
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    const next: Record<string, string> = {};
    if (hasPassword && !currentPassword) next.currentPassword = "Current password is required";
    if (newPassword.length < 8) next.newPassword = "Password must be at least 8 characters";
    if (confirmPassword !== newPassword) next.confirmPassword = "Passwords do not match";
    if (Object.keys(next).length > 0) {
      setFieldErrors(next);
      return;
    }
    mutation.mutate();
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <h2 className="text-lg font-semibold text-foreground" data-testid="text-change-password-title" data-tour="profile-security">
          {hasPassword ? "Change password" : "Set a password"}
        </h2>
        {hasPassword && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
            data-testid="badge-password-set"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Password is set
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {hasPassword
          ? "Enter your current password and choose a new one."
          : "You currently sign in with email codes only. Set a password as another way to log in."}
      </p>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        {hasPassword && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Current password</label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); clearFieldError("currentPassword"); }}
              autoComplete="current-password"
              className={fieldErrors.currentPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
              data-testid="input-current-password"
            />
            {fieldErrors.currentPassword && (
              <p className="text-xs text-red-600 mt-1" data-testid="error-current-password">{fieldErrors.currentPassword}</p>
            )}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">New password</label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); clearFieldError("newPassword"); }}
            autoComplete="new-password"
            className={fieldErrors.newPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
            data-testid="input-new-password"
          />
          {fieldErrors.newPassword && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-new-password">{fieldErrors.newPassword}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Confirm new password</label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError("confirmPassword"); }}
            autoComplete="new-password"
            className={fieldErrors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
            data-testid="input-confirm-password"
          />
          {fieldErrors.confirmPassword && (
            <p className="text-xs text-red-600 mt-1" data-testid="error-confirm-password">{fieldErrors.confirmPassword}</p>
          )}
        </div>
        <Button
          type="submit"
          disabled={mutation.isPending}
          className="bg-brand hover:bg-brand-hover"
          data-testid="button-submit-password"
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (hasPassword ? "Update password" : "Set password")}
        </Button>
      </form>

      {hasPassword && (
        <div className="mt-6 max-w-md">
          <Button
            type="button"
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={() => { setRemovePassword(""); setRemoveError(null); setRemoveOpen(true); }}
            data-testid="button-open-remove-password"
          >
            Remove password
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            After removing your password, you'll sign in using email codes only.
          </p>
        </div>
      )}

      <Dialog open={removeOpen} onOpenChange={(open) => { setRemoveOpen(open); if (!open) { setRemovePassword(""); setRemoveError(null); } }}>
        <DialogContent data-testid="dialog-remove-password">
          <DialogHeader>
            <DialogTitle>Remove password</DialogTitle>
            <DialogDescription>
              Enter your current password to confirm. After removal, you'll sign in with email codes only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">Current password</label>
            <Input
              type="password"
              value={removePassword}
              onChange={(e) => { setRemovePassword(e.target.value); setRemoveError(null); }}
              autoComplete="current-password"
              data-testid="input-remove-current-password"
            />
            {removeError && (
              <p className="text-xs text-red-600" data-testid="error-remove-password">{removeError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRemoveOpen(false)}
              disabled={removeMutation.isPending}
              data-testid="button-cancel-remove-password"
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={removeMutation.isPending || !removePassword}
              onClick={() => removeMutation.mutate()}
              data-testid="button-confirm-remove-password"
            >
              {removeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type TotpStatus = {
  totpEnabled: boolean;
  totpEnrolledAt: string | null;
  backupCodesRemaining: number;
};

function AdminTotpSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showNewCodes, setShowNewCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetEmailCode, setResetEmailCode] = useState("");
  const [resetCodeError, setResetCodeError] = useState("");

  const totpStatusQuery = useQuery<TotpStatus>({
    queryKey: ["/api/app/admin/totp/status"],
  });

  const authMeQuery = useQuery<AuthMeData>({
    queryKey: ["/api/app/auth/me"],
  });

  const sendCodeMutation = useMutation({
    mutationFn: async () => {
      const email = authMeQuery.data?.user?.email;
      if (!email) throw new Error("Could not determine your email.");
      const res = await apiRequest("POST", "/api/app/auth/request-code", { email });
      return res.json();
    },
    onSuccess: () => {
      setResetCodeSent(true);
      setResetCodeError("");
    },
    onError: (err: Error) => {
      setResetCodeError(parseErrorMessage(err, "Failed to send verification code."));
    },
  });

  const resetMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (emailCode: string) => {
      const res = await apiRequest("POST", "/api/app/admin/totp/reset", { emailCode });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "TOTP reset", description: "Your TOTP has been reset. You'll be prompted to re-enroll on next login." });
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/totp/status"] });
      setResetDialogOpen(false);
      setResetCodeSent(false);
      setResetEmailCode("");
      setResetCodeError("");
    },
    onError: (err: Error) => {
      setResetCodeError(parseErrorMessage(err, "Invalid or expired code. Please try again."));
    },
  });

  const regenMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/admin/totp/regenerate-backup-codes", {});
      return res.json() as Promise<{ success: boolean; backupCodes: string[] }>;
    },
    onSuccess: (data) => {
      setShowNewCodes(data.backupCodes);
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/totp/status"] });
      toast({ title: "Backup codes regenerated", description: "Save your new backup codes securely." });
    },
    onError: (err: Error) => {
      toast({ title: "Failed", description: parseErrorMessage(err, "Could not regenerate backup codes."), variant: "destructive" });
    },
  });

  const copyNewCodes = () => {
    if (!showNewCodes) return;
    navigator.clipboard.writeText(showNewCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
    setResetCodeSent(false);
    setResetEmailCode("");
    setResetCodeError("");
  };

  const status = totpStatusQuery.data;

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-1">Two-Factor Authentication (Admin)</h2>
      <p className="text-sm text-muted-foreground mb-4">
        As a platform admin, TOTP is required for your account. You can manage your authenticator and backup codes here.
      </p>

      {totpStatusQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : (
        <div className="border rounded-lg p-4 bg-muted space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status</span>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${status?.totpEnabled ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid="status-totp">
              {status?.totpEnabled ? "Enrolled" : "Not enrolled"}
            </span>
          </div>
          {status?.totpEnrolledAt && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Enrolled</span>
              <span className="text-sm text-foreground" data-testid="text-totp-enrolled-at">{new Date(status.totpEnrolledAt).toLocaleDateString()}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Backup codes remaining</span>
            <span className={`text-sm font-medium ${(status?.backupCodesRemaining ?? 0) <= 2 ? "text-amber-600" : "text-foreground"}`} data-testid="text-backup-remaining">
              {status?.backupCodesRemaining ?? 0}
            </span>
          </div>
        </div>
      )}

      {showNewCodes && (
        <div className="border border-green-200 rounded-lg p-4 bg-green-50 mb-4">
          <p className="text-sm font-semibold text-green-800 mb-3">New backup codes — save these now:</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {showNewCodes.map((code, i) => (
              <div key={i} className="font-mono text-sm bg-white border border-green-200 rounded px-3 py-1.5 text-center text-foreground" data-testid={`new-backup-code-${i}`}>{code}</div>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={copyNewCodes} className="text-xs" data-testid="button-copy-new-codes">
            {copied ? "Copied!" : "Copy all codes"}
          </Button>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => regenMutation.mutate()}
          disabled={regenMutation.isPending || !status?.totpEnabled}
          data-testid="button-regen-backup-codes"
        >
          {regenMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
          Regenerate backup codes
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 border-red-200 hover:bg-red-50"
          onClick={handleOpenResetDialog}
          data-testid="button-reset-totp"
        >
          <ShieldOff className="h-4 w-4 mr-1.5" />
          Reset TOTP authenticator
        </Button>
      </div>

      <Dialog open={resetDialogOpen} onOpenChange={(open) => { if (!open && (sendCodeMutation.isPending || resetMutation.isPending)) return; setResetDialogOpen(open); if (!open) { setResetCodeSent(false); setResetEmailCode(""); setResetCodeError(""); } }}>
        <DialogContent
          data-testid="dialog-reset-totp"
          onEscapeKeyDown={(e) => { if (sendCodeMutation.isPending || resetMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (sendCodeMutation.isPending || resetMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (sendCodeMutation.isPending || resetMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Reset TOTP authenticator</DialogTitle>
            <DialogDescription>
              This will remove your current authenticator setup. You'll need to re-enroll on your next login.
              To confirm, we need to verify your identity via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!resetCodeSent ? (
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  We'll send a one-time verification code to <strong>{authMeQuery.data?.user?.email}</strong>.
                </p>
                {resetCodeError && <p className="text-sm text-red-600 mb-2">{resetCodeError}</p>}
                <Button
                  onClick={() => sendCodeMutation.mutate()}
                  disabled={sendCodeMutation.isPending}
                  className="w-full"
                  data-testid="button-send-reset-code"
                >
                  {sendCodeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                  Send verification code
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-3">Enter the code we sent to your email:</p>
                <Input
                  value={resetEmailCode}
                  onChange={(e) => { setResetEmailCode(e.target.value); setResetCodeError(""); }}
                  placeholder="6-digit code"
                  maxLength={6}
                  className="text-center tracking-widest font-mono text-lg mb-2"
                  data-testid="input-reset-email-code"
                />
                {resetCodeError && <p className="text-sm text-red-600 mb-2">{resetCodeError}</p>}
                <p className="text-xs text-muted-foreground">
                  Didn't receive it?{" "}
                  <button className="text-blue-600 hover:underline" onClick={() => { setResetCodeSent(false); setResetEmailCode(""); }} data-testid="link-resend-reset-code">
                    Resend code
                  </button>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} data-testid="button-cancel-reset">Cancel</Button>
            {resetCodeSent && (
              <Button
                variant="destructive"
                onClick={() => resetMutation.mutate(resetEmailCode)}
                disabled={resetMutation.isPending || resetEmailCode.length < 6}
                data-testid="button-confirm-reset-totp"
              >
                {resetMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm reset
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type TrustedDevice = {
  id: number;
  userId: number;
  deviceHash: string;
  userAgent: string | null;
  lastIp: string | null;
  lastLocation: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  trustedAt: string | null;
  isCurrent: boolean;
};

function parseUserAgent(ua: string | null): { name: string; icon: typeof Monitor } {
  if (!ua) return { name: "Unknown device", icon: Monitor };
  const lower = ua.toLowerCase();
  if (lower.includes("mobile") || lower.includes("android") || lower.includes("iphone") || lower.includes("ipad")) {
    let name = "Mobile browser";
    if (lower.includes("chrome")) name = "Chrome Mobile";
    else if (lower.includes("safari") && lower.includes("iphone")) name = "Safari on iPhone";
    else if (lower.includes("safari") && lower.includes("ipad")) name = "Safari on iPad";
    else if (lower.includes("firefox")) name = "Firefox Mobile";
    return { name, icon: Smartphone };
  }
  let browser = "Unknown browser";
  if (lower.includes("edg/")) browser = "Edge";
  else if (lower.includes("chrome")) browser = "Chrome";
  else if (lower.includes("firefox")) browser = "Firefox";
  else if (lower.includes("safari")) browser = "Safari";
  let os = "";
  if (lower.includes("windows")) os = " on Windows";
  else if (lower.includes("mac")) os = " on macOS";
  else if (lower.includes("linux")) os = " on Linux";
  return { name: `${browser}${os}`, icon: Monitor };
}

function TrustedDevicesSection() {
  const { toast } = useToast();
  const { data, isLoading, refetch } = useQuery<{ devices: TrustedDevice[] }>({
    queryKey: ["/api/app/profile/devices"],
  });

  const trustMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/app/profile/devices/${id}/trust`),
    onSuccess: () => { refetch(); toast({ title: "Device trusted" }); },
    onError: () => toast({ title: "Failed to trust device", variant: "destructive" }),
  });

  const untrustMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/app/profile/devices/${id}/untrust`),
    onSuccess: () => { refetch(); toast({ title: "Device untrusted" }); },
    onError: () => toast({ title: "Failed to untrust device", variant: "destructive" }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/app/profile/devices/${id}`),
    onSuccess: () => { refetch(); toast({ title: "Device removed" }); },
    onError: () => toast({ title: "Failed to remove device", variant: "destructive" }),
  });

  const devices = data?.devices ?? [];

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-1">Trusted Devices</h2>
      <p className="text-sm text-muted-foreground mb-4">Trusted devices won't trigger a new sign-in alert email. Sign-ins from untrusted devices will send a security notification.</p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading devices...
        </div>
      ) : devices.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No devices on record yet. Sign in again to see your device here.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="table-trusted-devices">
            <thead>
              <tr className="border-b bg-muted">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Device</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Location / IP</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">First seen / Trusted</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => {
                const { name, icon: DevIcon } = parseUserAgent(device.userAgent);
                const trusted = !!device.trustedAt;
                const isPending = trustMutation.isPending || untrustMutation.isPending || removeMutation.isPending;
                return (
                  <tr key={device.id} className="border-b last:border-0" data-testid={`row-device-${device.id}`}>
                    <td className="px-4 py-3 text-foreground">
                      <div className="flex items-center gap-2">
                        <DevIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <span className="font-medium">{name}</span>
                          {device.isCurrent && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" data-testid={`badge-current-${device.id}`}>This device</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                      <div>{device.lastLocation || "—"}</div>
                      <div className="text-xs">{device.lastIp || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${trusted ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" : "bg-muted text-muted-foreground"}`} data-testid={`status-device-${device.id}`}>
                        {trusted ? "Trusted" : "Not trusted"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell text-xs">
                      {trusted && device.trustedAt ? (
                        <div>
                          <div className="font-medium text-green-700 dark:text-green-400">Trusted {new Date(device.trustedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                          <div className="text-xs">First seen {new Date(device.firstSeenAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        </div>
                      ) : (
                        <div>First seen {new Date(device.firstSeenAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {trusted ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => untrustMutation.mutate(device.id)}
                            disabled={isPending}
                            data-testid={`button-untrust-${device.id}`}
                          >
                            Untrust
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => trustMutation.mutate(device.id)}
                            disabled={isPending}
                            data-testid={`button-trust-${device.id}`}
                          >
                            Trust
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={isPending} data-testid={`button-remove-${device.id}`}>
                              Remove
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove device?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove the device from your list. The next sign-in from this device will trigger a security alert email.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeMutation.mutate(device.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type UserTotpEnrollStep = "idle" | "qr" | "confirm" | "backup";

function UserTotpSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [enrollStep, setEnrollStep] = useState<UserTotpEnrollStep>("idle");
  const [qrData, setQrData] = useState<{ secret: string; qrCode: string } | null>(null);
  const [totpCode, setTotpCode] = useState(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);
  const [justEnrolledCodes, setJustEnrolledCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedEnroll, setCopiedEnroll] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCodeSent, setDisableCodeSent] = useState(false);
  const [disableEmailCode, setDisableEmailCode] = useState("");
  const [disableCodeError, setDisableCodeError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const authMeQuery = useQuery<AuthMeData>({ queryKey: ["/api/app/auth/me"] });

  const totpStatusQuery = useQuery<TotpStatus>({
    queryKey: ["/api/app/profile/totp/status"],
  });

  const startEnrollMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/app/profile/totp/enroll-start", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to start enrollment");
      return res.json() as Promise<{ secret: string; qrCode: string }>;
    },
    onSuccess: (data) => {
      setQrData(data);
      setTotpCode(["", "", "", "", "", ""]);
      setCodeError(null);
      setEnrollStep("qr");
    },
    onError: (err: Error) => {
      toast({ title: "Failed to start enrollment", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  const confirmEnrollMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", "/api/app/profile/totp/enroll-confirm", { totpCode: code });
      return res.json() as Promise<{ success: boolean; backupCodes: string[] }>;
    },
    onSuccess: (data) => {
      setCodeError(null);
      setJustEnrolledCodes(data.backupCodes);
      setEnrollStep("backup");
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile/totp/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      toast({ title: "2FA enabled", description: "Two-factor authentication is now active on your account." });
    },
    onError: (err: Error) => {
      const message = parseErrorMessage(err, "Invalid code. Please try again.");
      setCodeError(message);
      setTotpCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    },
  });

  const regenMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/profile/totp/regenerate-backup-codes", {});
      return res.json() as Promise<{ success: boolean; backupCodes: string[] }>;
    },
    onSuccess: (data) => {
      setNewBackupCodes(data.backupCodes);
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile/totp/status"] });
      toast({ title: "Backup codes regenerated", description: "Save your new backup codes securely." });
    },
    onError: (err: Error) => {
      toast({ title: "Failed", description: parseErrorMessage(err, "Could not regenerate backup codes."), variant: "destructive" });
    },
  });

  const sendDisableCodeMutation = useMutation({
    mutationFn: async () => {
      const email = authMeQuery.data?.user?.email;
      if (!email) throw new Error("Could not determine your email.");
      const res = await apiRequest("POST", "/api/app/auth/request-code", { email });
      return res.json();
    },
    onSuccess: () => {
      setDisableCodeSent(true);
      setDisableCodeError("");
    },
    onError: (err: Error) => {
      setDisableCodeError(parseErrorMessage(err, "Failed to send verification code."));
    },
  });

  const disableMutation = useMutation({
    mutationFn: async (emailCode: string) => {
      const res = await apiRequest("POST", "/api/app/profile/totp/disable", { emailCode });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "2FA disabled", description: "Two-factor authentication has been turned off." });
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile/totp/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      setDisableDialogOpen(false);
      setDisableCodeSent(false);
      setDisableEmailCode("");
      setDisableCodeError("");
      setEnrollStep("idle");
      setJustEnrolledCodes(null);
      setNewBackupCodes(null);
    },
    onError: (err: Error) => {
      setDisableCodeError(parseErrorMessage(err, "Invalid or expired code. Please try again."));
    },
  });

  const handleTotpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    if (codeError) setCodeError(null);
    const newCode = [...totpCode];
    newCode[index] = value.slice(-1);
    setTotpCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    const fullCode = newCode.join("");
    if (fullCode.length === 6) confirmEnrollMutation.mutate(fullCode);
  };

  const handleTotpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !totpCode[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    if (codeError) setCodeError(null);
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...totpCode];
    for (let i = 0; i < 6; i++) newCode[i] = pasted[i] || "";
    setTotpCode(newCode);
    if (pasted.length === 6) confirmEnrollMutation.mutate(pasted);
  };

  const status = totpStatusQuery.data;

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-1">Two-Factor Authentication</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Add an extra layer of security to your account. When enabled, you'll need a 6-digit code from your authenticator app each time you sign in.{" "}
        <span className="font-medium text-foreground">(Recommended)</span>
      </p>

      {totpStatusQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : (
        <>
          <div className="border rounded-lg p-4 bg-muted space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${status?.totpEnabled ? "bg-green-100 text-green-700" : "bg-muted-foreground/10 text-muted-foreground"}`} data-testid="status-user-totp">
                {status?.totpEnabled ? "Enabled" : "Not enabled"}
              </span>
            </div>
            {status?.totpEnabled && status.totpEnrolledAt && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Enabled since</span>
                <span className="text-sm text-foreground">{new Date(status.totpEnrolledAt).toLocaleDateString()}</span>
              </div>
            )}
            {status?.totpEnabled && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Backup codes remaining</span>
                <span className={`text-sm font-medium ${(status.backupCodesRemaining ?? 0) <= 2 ? "text-amber-600" : "text-foreground"}`} data-testid="text-user-backup-remaining">
                  {status.backupCodesRemaining ?? 0}
                </span>
              </div>
            )}
          </div>

          {!status?.totpEnabled && enrollStep === "idle" && (
            <Button
              onClick={() => startEnrollMutation.mutate()}
              disabled={startEnrollMutation.isPending}
              data-testid="button-user-enable-totp"
              className="bg-brand hover:bg-brand-hover text-white"
            >
              {startEnrollMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enable two-factor authentication
            </Button>
          )}

          {enrollStep === "qr" && qrData && (
            <div className="border rounded-lg p-5 bg-background space-y-4">
              <p className="text-sm font-semibold text-foreground">Step 1 — Scan this QR code with your authenticator app</p>
              <p className="text-xs text-muted-foreground">Use Google Authenticator, Authy, 1Password, or any TOTP-compatible app.</p>
              <div className="flex justify-center">
                <img src={qrData.qrCode} alt="TOTP QR Code" className="w-44 h-44 border border-border rounded-lg" data-testid="img-user-totp-qr" />
              </div>
              <p className="text-xs text-muted-foreground text-center">Can't scan? Enter this code manually:</p>
              <div className="bg-muted rounded-lg px-4 py-2 text-center">
                <span className="font-mono text-xs text-foreground break-all" data-testid="text-user-totp-secret">{qrData.secret}</span>
              </div>
              <Button className="w-full bg-brand hover:bg-brand-hover text-white" onClick={() => setEnrollStep("confirm")} data-testid="button-user-enroll-next">
                I've scanned the code — next
              </Button>
              <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => { setEnrollStep("idle"); setQrData(null); }} data-testid="button-user-enroll-cancel">
                Cancel
              </Button>
            </div>
          )}

          {enrollStep === "confirm" && (
            <div className="border rounded-lg p-5 bg-background space-y-4">
              <p className="text-sm font-semibold text-foreground">Step 2 — Enter the 6-digit code from your authenticator app</p>
              <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                {totpCode.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleTotpChange(i, e.target.value)}
                    onKeyDown={(e) => handleTotpKeyDown(i, e)}
                    autoFocus={i === 0}
                    className={`w-11 h-13 text-center text-xl font-bold border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-brand ${codeError ? "border-red-500" : "border-border"}`}
                    data-testid={`input-user-totp-${i}`}
                  />
                ))}
              </div>
              {codeError && <p className="text-xs text-red-600 text-center" data-testid="error-user-totp-code">{codeError}</p>}
              {confirmEnrollMutation.isPending && <div className="flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setEnrollStep("qr")} data-testid="button-user-enroll-back">Back to QR code</Button>
                <Button variant="ghost" size="sm" className="flex-1 text-muted-foreground" onClick={() => { setEnrollStep("idle"); setQrData(null); setTotpCode(["","","","","",""]); setCodeError(null); }} data-testid="button-user-confirm-cancel">Cancel</Button>
              </div>
            </div>
          )}

          {enrollStep === "backup" && justEnrolledCodes && (
            <div className="border border-green-200 rounded-lg p-5 bg-green-50 space-y-3 mb-4">
              <p className="text-sm font-semibold text-green-800">Two-factor authentication enabled! Save your backup codes.</p>
              <p className="text-xs text-green-700">Each code can only be used once if you lose access to your authenticator app. Store them somewhere safe.</p>
              <div className="grid grid-cols-2 gap-2">
                {justEnrolledCodes.map((code, i) => (
                  <div key={i} className="font-mono text-sm bg-white border border-green-200 rounded px-3 py-1.5 text-center text-foreground" data-testid={`enrolled-backup-code-${i}`}>{code}</div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(justEnrolledCodes.join("\n")); setCopiedEnroll(true); setTimeout(() => setCopiedEnroll(false), 2000); }} className="text-xs" data-testid="button-copy-enrolled-codes">
                {copiedEnroll ? "Copied!" : "Copy all codes"}
              </Button>
            </div>
          )}

          {status?.totpEnabled && (
            <>
              {newBackupCodes && (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50 mb-4">
                  <p className="text-sm font-semibold text-green-800 mb-3">New backup codes — save these now:</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {newBackupCodes.map((code, i) => (
                      <div key={i} className="font-mono text-sm bg-white border border-green-200 rounded px-3 py-1.5 text-center text-foreground" data-testid={`user-new-backup-code-${i}`}>{code}</div>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(newBackupCodes.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="text-xs" data-testid="button-copy-user-new-codes">
                    {copied ? "Copied!" : "Copy all codes"}
                  </Button>
                </div>
              )}

              <div className="flex gap-3 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => regenMutation.mutate()} disabled={regenMutation.isPending} data-testid="button-user-regen-backup-codes">
                  {regenMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                  Regenerate backup codes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => { setDisableDialogOpen(true); setDisableCodeSent(false); setDisableEmailCode(""); setDisableCodeError(""); }}
                  data-testid="button-user-disable-totp"
                >
                  <ShieldOff className="h-4 w-4 mr-1.5" />
                  Disable two-factor authentication
                </Button>
              </div>
            </>
          )}
        </>
      )}

      <Dialog open={disableDialogOpen} onOpenChange={(open) => { if (!open && (sendDisableCodeMutation.isPending || disableMutation.isPending)) return; setDisableDialogOpen(open); if (!open) { setDisableCodeSent(false); setDisableEmailCode(""); setDisableCodeError(""); } }}>
        <DialogContent
          data-testid="dialog-disable-totp"
          onEscapeKeyDown={(e) => { if (sendDisableCodeMutation.isPending || disableMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (sendDisableCodeMutation.isPending || disableMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (sendDisableCodeMutation.isPending || disableMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication</DialogTitle>
            <DialogDescription>
              This will remove 2FA from your account. You'll only need your email code to sign in afterward.
              To confirm, we'll verify your identity via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!disableCodeSent ? (
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  We'll send a one-time code to <strong>{authMeQuery.data?.user?.email}</strong>.
                </p>
                {disableCodeError && <p className="text-sm text-red-600 mb-2">{disableCodeError}</p>}
                <Button onClick={() => sendDisableCodeMutation.mutate()} disabled={sendDisableCodeMutation.isPending} className="w-full" data-testid="button-send-disable-code">
                  {sendDisableCodeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                  Send verification code
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-3">Enter the code we sent to your email:</p>
                <Input
                  value={disableEmailCode}
                  onChange={(e) => { setDisableEmailCode(e.target.value); setDisableCodeError(""); }}
                  placeholder="6-digit code"
                  maxLength={6}
                  className="text-center tracking-widest font-mono text-lg mb-2"
                  data-testid="input-disable-email-code"
                />
                {disableCodeError && <p className="text-sm text-red-600 mb-2">{disableCodeError}</p>}
                <p className="text-xs text-muted-foreground">
                  Didn't receive it?{" "}
                  <button className="text-blue-600 hover:underline" onClick={() => { setDisableCodeSent(false); setDisableEmailCode(""); }} data-testid="link-resend-disable-code">
                    Resend code
                  </button>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableDialogOpen(false)} data-testid="button-cancel-disable">Cancel</Button>
            {disableCodeSent && (
              <Button variant="destructive" onClick={() => disableMutation.mutate(disableEmailCode)} disabled={disableMutation.isPending || disableEmailCode.length < 6} data-testid="button-confirm-disable-totp">
                {disableMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm disable
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SecurityTab({ profile, isAdmin }: { profile: ProfileData; isAdmin?: boolean }) {
  return (
    <div className="mt-8 space-y-10">
      <div data-tour="profile-security-password">
        <ChangePasswordForm hasPassword={profile.hasPassword} />
      </div>
      <div data-tour="profile-security-2fa">
        {isAdmin ? <AdminTotpSection /> : <UserTotpSection />}
      </div>

      <SavedLoginPreferenceSection email={profile.email} />

      <div className="space-y-10" data-tour="profile-security-devices">
        <ActiveSessionsSection />

        <TrustedDevicesSection />
      </div>
    </div>
  );
}

const LOGIN_METHOD_STORAGE_KEY = (email: string) => `padpulse_login_method_${email.toLowerCase()}`;

function SavedLoginPreferenceSection({ email }: { email: string }) {
  const { toast } = useToast();
  const [savedMethod, setSavedMethod] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      return localStorage.getItem(LOGIN_METHOD_STORAGE_KEY(email));
    } catch {
      return null;
    }
  });

  const label =
    savedMethod === "password"
      ? "Password"
      : savedMethod === "email-code"
        ? "Email code"
        : "Email code (default)";

  const handleClear = () => {
    try {
      localStorage.removeItem(LOGIN_METHOD_STORAGE_KEY(email));
      setSavedMethod(null);
      toast({
        title: "Login preference cleared",
        description: "You'll get the email-code option by default next time you sign in.",
      });
    } catch {
      toast({
        title: "Couldn't clear preference",
        description: "Your browser blocked access to local storage.",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-1">Saved Login Preference</h2>
      <p className="text-sm text-muted-foreground mb-4">
        We remember the last sign-in method you used on this browser so we can take you straight to it next time.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-foreground">
          Current preference:{" "}
          <span className="font-medium" data-testid="text-saved-login-preference">{label}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={!savedMethod}
          data-testid="button-clear-login-preference"
        >
          Reset to default
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Resetting only affects this browser. The default sign-in method is the email code.
      </p>
    </div>
  );
}

type ActiveSession = {
  sid: string;
  userAgent: string | null;
  ipAddress: string | null;
  location: string | null;
  loginAt: string | null;
  lastActiveAt: string;
  expiresAt: string;
  isCurrent: boolean;
};

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const diffMs = Date.now() - then;
  if (diffMs < 0) return "just now";
  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function ActiveSessionsSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { data, isLoading, refetch } = useQuery<{ sessions: ActiveSession[] }>({
    queryKey: ["/api/app/profile/sessions"],
  });

  const revokeMutation = useMutation({
    mutationFn: async (sid: string) => {
      const res = await apiRequest("DELETE", `/api/app/profile/sessions/${encodeURIComponent(sid)}`);
      return res.json() as Promise<{ success: boolean; signedOutCurrent?: boolean }>;
    },
    onSuccess: (result) => {
      if (result.signedOutCurrent) {
        queryClient.clear();
        toast({ title: "Signed out", description: "You've been signed out of this device." });
        navigate("/app/login");
        return;
      }
      toast({ title: "Session signed out" });
      refetch();
    },
    onError: () => toast({ title: "Failed to sign out session", variant: "destructive" }),
  });

  const sessions = data?.sessions ?? [];

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-1">Active Sessions</h2>
      <p className="text-sm text-muted-foreground mb-4">
        These are the browsers and devices currently signed into your account. Sign out of any session you don't recognize.
      </p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sessions...
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4" data-testid="text-no-sessions">No active sessions found.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="table-active-sessions">
            <thead>
              <tr className="border-b bg-muted">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Device</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Location / IP</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Last active</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Signed in</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => {
                const { name, icon: DevIcon } = parseUserAgent(session.userAgent);
                const pending = revokeMutation.isPending && revokeMutation.variables === session.sid;
                return (
                  <tr key={session.sid} className="border-b last:border-0" data-testid={`row-session-${session.sid}`}>
                    <td className="px-4 py-3 text-foreground">
                      <div className="flex items-center gap-2">
                        <DevIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <span className="font-medium" data-testid={`text-session-device-${session.sid}`}>{name}</span>
                          {session.isCurrent && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" data-testid={`badge-current-session-${session.sid}`}>This session</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                      <div className="flex items-center gap-1.5">
                        <Globe className="h-3 w-3 flex-shrink-0" />
                        <span data-testid={`text-session-location-${session.sid}`}>{session.location || "—"}</span>
                      </div>
                      <div className="text-xs ml-4">{session.ipAddress || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground" data-testid={`text-session-last-active-${session.sid}`}>
                      {formatRelativeTime(session.lastActiveAt)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell text-xs" data-testid={`text-session-login-${session.sid}`}>
                      {session.loginAt
                        ? new Date(session.loginAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            disabled={pending}
                            data-testid={`button-signout-session-${session.sid}`}
                          >
                            {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
                            Sign out
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Sign out of this session?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {session.isCurrent
                                ? "You'll be signed out on this device and sent back to the login screen."
                                : "The browser or device using this session will be signed out immediately."}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => revokeMutation.mutate(session.sid)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              data-testid={`button-confirm-signout-${session.sid}`}
                            >
                              Sign out
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MembershipTab({ membership }: { membership: ActiveMembership }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const leaveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/team-members/leave", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.clear();
      toast({
        title: "You left the team",
        description: `You're no longer a member of ${membership.ownerName}'s team.`,
      });
      navigate("/app/no-team");
    },
    onError: (err: Error) => {
      toast({
        title: "Couldn't leave the team",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const roleLabel = membership.role
    ? membership.role.charAt(0).toUpperCase() + membership.role.slice(1)
    : "Member";

  return (
    <div className="mt-8">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground" data-testid="text-membership-title">
          Team membership
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          You have access to this team and the properties they share with you.
        </p>
      </div>

      <div className="rounded-lg border border-border p-5 max-w-xl">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-brand" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground" data-testid="text-membership-owner">
              You're a member of {membership.ownerName}'s team
            </p>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="text-membership-owner-email">
              {membership.ownerEmail}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Role</span>
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-foreground"
                data-testid="badge-membership-role"
              >
                {roleLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 max-w-xl">
        <h3 className="text-sm font-semibold text-foreground">Leave this team</h3>
        <p className="text-sm text-muted-foreground mt-1">
          You'll lose access to {membership.ownerName}'s properties immediately. {membership.ownerName} will need to invite you again to restore access.
        </p>
        <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="mt-3 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-700"
              data-testid="button-leave-team"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Leave team
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="dialog-leave-team">
            <AlertDialogHeader>
              <AlertDialogTitle>Leave {membership.ownerName}'s team?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll be signed out and will lose access to {membership.ownerName}'s properties right away. This action can only be reversed by {membership.ownerName} re-inviting you.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-leave-team">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  leaveMutation.mutate();
                }}
                disabled={leaveMutation.isPending}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                data-testid="button-confirm-leave-team"
              >
                {leaveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Leave team"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function ChangeEmailDialog({ currentEmail }: { currentEmail: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"request" | "verify">("request");
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  function reset() {
    setStep("request");
    setNewEmail("");
    setCode("");
    setEmailError(null);
    setCodeError(null);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset();
  }

  const requestMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/profile/email/request-code", {
        email: newEmail.trim(),
      });
      return res.json();
    },
    onSuccess: () => {
      setEmailError(null);
      setStep("verify");
      toast({
        title: "Verification code sent",
        description: `We sent a 6-digit code to ${newEmail.trim()}.`,
      });
    },
    onError: (err: Error) => {
      const fields = parseFieldErrors(err);
      const message = parseErrorMessage(err, "Failed to send verification code.");
      setEmailError(fields.email || message);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/profile/email/confirm", {
        email: newEmail.trim(),
        code: code.trim(),
      });
      return res.json();
    },
    onSuccess: (data: { sessionsInvalidated?: number }) => {
      const invalidated = data?.sessionsInvalidated ?? 0;
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      const description = invalidated > 0
        ? `Your email is now ${newEmail.trim()}. We signed out ${invalidated} other ${invalidated === 1 ? "session" : "sessions"} for security.`
        : `Your email is now ${newEmail.trim()}.`;
      toast({ title: "Email updated", description });
      handleOpenChange(false);
    },
    onError: (err: Error) => {
      const message = parseErrorMessage(err, "Failed to verify code.");
      setCodeError(message);
    },
  });

  function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    setEmailError(null);
    const trimmed = newEmail.trim();
    if (!trimmed) {
      setEmailError("Please enter a new email address.");
      return;
    }
    if (trimmed.toLowerCase() === currentEmail.toLowerCase()) {
      setEmailError("That is already your current email address.");
      return;
    }
    requestMutation.mutate();
  }

  function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setCodeError(null);
    if (code.trim().length < 6) {
      setCodeError("Enter the 6-digit code from your email.");
      return;
    }
    confirmMutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        type="button"
        variant="outline"
        onClick={() => handleOpenChange(true)}
        data-testid="button-change-email"
      >
        Change
      </Button>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="dialog-change-email">
        <DialogHeader>
          <DialogTitle>
            {step === "request" ? "Change your email" : "Verify your new email"}
          </DialogTitle>
          <DialogDescription>
            {step === "request"
              ? "Enter the new email address you want to use. We'll send a 6-digit code to confirm you own it."
              : `Enter the 6-digit code we just sent to ${newEmail.trim()}. The code expires in 10 minutes.`}
          </DialogDescription>
        </DialogHeader>

        {step === "request" ? (
          <form onSubmit={handleRequest} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">New email</label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); setEmailError(null); }}
                placeholder="you@example.com"
                autoComplete="email"
                className={emailError ? "border-red-500 focus-visible:ring-red-500" : ""}
                data-testid="input-new-email"
              />
              {emailError && (
                <p className="text-xs text-red-600 mt-1" data-testid="error-new-email">{emailError}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                data-testid="button-cancel-change-email"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={requestMutation.isPending}
                className="bg-brand hover:bg-brand-hover"
                data-testid="button-send-email-code"
              >
                {requestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={handleConfirm} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Verification code</label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setCodeError(null); }}
                placeholder="123456"
                autoComplete="one-time-code"
                className={codeError ? "border-red-500 focus-visible:ring-red-500" : ""}
                data-testid="input-email-code"
              />
              {codeError && (
                <p className="text-xs text-red-600 mt-1" data-testid="error-email-code">{codeError}</p>
              )}
              <button
                type="button"
                onClick={() => { setCode(""); setCodeError(null); setStep("request"); }}
                className="text-xs text-blue-600 hover:underline mt-2"
                data-testid="button-change-email-back"
              >
                Use a different email address
              </button>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                data-testid="button-cancel-verify-email"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={confirmMutation.isPending}
                className="bg-brand hover:bg-brand-hover"
                data-testid="button-confirm-email-code"
              >
                {confirmMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify and update"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
